// FingerprintJS Pro device-signal loader — ENV-GATED, fully inert when off.
//
// When VITE_FPJS_PUBLIC_KEY is set, we lazily load the FingerprintJS Pro
// browser agent and collect a (requestId, visitorId) pair to send alongside
// the OTP-verify request. The auth-service backend reads these as OPTIONAL
// signals; they must never gate or break the login flow.
//
// Design constraints:
//   - Key absent  -> getDeviceSignals() returns {} without importing the SDK,
//     so the whole @fingerprintjs/fingerprintjs-pro chunk is never fetched.
//   - The agent is imported dynamically and the load() promise is cached at
//     module scope so we initialise the agent at most once per page session.
//   - ANY failure (network, SDK error, timeout) resolves to {} — fingerprinting
//     must NEVER block or throw in the login path.

import type { Agent } from "@fingerprintjs/fingerprintjs-pro";

const apiKey = import.meta.env.VITE_FPJS_PUBLIC_KEY;
const region = (import.meta.env.VITE_FPJS_REGION || "eu") as
  | "us"
  | "eu"
  | "ap";

export function fingerprintEnabled(): boolean {
  return typeof apiKey === "string" && apiKey.length > 0;
}

// Cached agent-load promise — created on first use, reused thereafter.
let agentPromise: Promise<Agent> | null = null;

async function getAgent(): Promise<Agent> {
  if (!agentPromise) {
    const { load } = await import("@fingerprintjs/fingerprintjs-pro");
    agentPromise = load({ apiKey: apiKey as string, region });
  }
  return agentPromise;
}

export interface DeviceSignals {
  request_id?: string;
  visitor_id?: string;
}

/**
 * getDeviceSignals resolves to the FingerprintJS Pro request/visitor id pair,
 * or {} when fingerprinting is disabled or anything goes wrong. Never throws.
 *
 * When a linkedId is supplied (e.g. the account email), it is passed through to
 * the Fingerprint Pro agent so device events are attributable to the account.
 */
export async function getDeviceSignals(
  linkedId?: string,
): Promise<DeviceSignals> {
  if (!fingerprintEnabled()) return {};
  try {
    const agent = await getAgent();
    const res = await agent.get(linkedId ? { linkedId } : undefined);
    return { request_id: res.requestId, visitor_id: res.visitorId };
  } catch {
    // Swallow everything — login must proceed without device signals.
    return {};
  }
}
